import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FilingFormStateService } from './filing-form-state.service';
import { ConversionValidationCacheService } from './conversion-validation-cache.service';
import { FilingFormPersistenceService } from './filing-form-persistence.service';
import { ROUTE_RELOAD_HOP_URL } from '../../components/common/route-reload-hop/route-reload-hop.component';

/**
 * Owns the `mbrs-current-sheet-{conversionId}` pointer that remembers the
 * last-visited sheet within a filing form so a mid-form page refresh can
 * restore the user's place. The pointer now lives in IndexedDB via
 * {@link FilingFormPersistenceService} — there is no longer a
 * `mbrs-current-sheet-*` localStorage key.
 *
 * Centralising read/write/clear here (per SOLID DIP — components should
 * not reach into the persistence layer directly) lets us invalidate the cache
 * from a few well-known seams without having to audit every caller:
 *   - leaving the filing form for ANY non-filing page — XBRL Files
 *     listing, dashboard, profile, user-management, preview, etc.
 *     (`flushConversionCaches`, driven by the NavigationEnd handler below)
 *   - after a successful submit-for-validation (`clear(conversionId)`)
 *   - per-conversion wipe inside `FilingFormStateService.clearAllStorage`
 *   - logout already wipes everything via `clearCustomerDataCache()`
 *     (prefix match on `mbrs-`).
 *
 * NOT invalidated on form-route teardown / refresh — that would defeat
 * the whole point of the cache. In-form sheet-to-sheet navigation
 * (`/mbrs/filing` ↔ `/mbrs/filing/role1` ↔ `/mbrs/filing/role2`) is also
 * preserved: those arrivals still match the filing prefix, so the flush
 * only fires when the *destination* is outside the form.
 */
@Injectable({ providedIn: 'root' })
export class CurrentSheetCacheService {
  // Every filing "sheet" route shares this prefix (`/mbrs/filing`,
  // `/mbrs/filing/role1`, `/mbrs/filing/role2`) — see app.routes.ts. It is
  // the single source of truth for "is this URL inside the filing form?",
  // used both to detect that we *came from* a sheet and that we did NOT
  // *arrive at* one.
  private static readonly FILING_ROUTE_PREFIX = '/mbrs/filing';

  private previousUrl = '';
  private readonly filingFormState = inject(FilingFormStateService);
  private readonly validationCache = inject(ConversionValidationCacheService);
  private readonly persistence = inject(FilingFormPersistenceService);

  constructor() {
    // Self-listen for navigation transitions OUT of the filing form into any
    // non-filing page (listing, dashboard, profile, preview, …). Anchoring
    // the policy here (vs each landing component's ngOnInit) keeps a single
    // seam: a fresh entry into the listing/dashboard from login or the
    // sidebar does NOT wipe the cache (previousUrl is not a filing route),
    // so in-form refresh-persistence survives the next walk-back into a
    // conversion.
    const router = inject(Router);
    router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const arrivedUrl = event.urlAfterRedirects;
        // Ignore `FilingFormContainerComponent.retryLoad`'s hop ENTIRELY — no
        // flush and no `previousUrl` update — so filing → ROUTE_RELOAD_HOP_URL →
        // filing reads as filing → filing. `skipLocationChange` does not
        // suppress NavigationEnd, so without this the hop looks like leaving
        // the form and Retry would wipe the cached edits it must preserve.
        if (arrivedUrl === ROUTE_RELOAD_HOP_URL) {
          return;
        }
        const cameFromFilingForm = this.previousUrl.startsWith(
          CurrentSheetCacheService.FILING_ROUTE_PREFIX,
        );
        const arrivedAtFilingForm = arrivedUrl.startsWith(
          CurrentSheetCacheService.FILING_ROUTE_PREFIX,
        );
        // Flush only when stepping from a sheet to a non-sheet page. The
        // `!arrivedAtFilingForm` guard deliberately exempts in-form sheet
        // hops (both previous and arrived URLs share the filing prefix).
        if (cameFromFilingForm && !arrivedAtFilingForm) {
          this.flushConversionCaches();
        }
        this.previousUrl = arrivedUrl;
      });
  }

  /**
   * Wipes every per-conversion cache that becomes stale the moment the user
   * leaves the filing form. We co-locate these four wipes because they share
   * one lifecycle: all are per-conversion, all are stale once the form is
   * left, and all are already wiped together by logout. Re-entering any
   * conversion repopulates them via `loadFromAPI()`.
   *   - `mbrs-current-sheet-*` — now in IndexedDB via
   *     FilingFormPersistenceService (clearAll)
   *   - `mbrs-filing-form-{original,modified,metadata}-*` — baseline /
   *     modified payloads, also in IndexedDB via
   *     FilingFormPersistenceService (clearAllPersistedFormState)
   *   - the in-memory companion to that persisted wipe
   *     (clearAllInMemoryFormState) — without it, the same-id revisit path
   *     bypasses setConversionId's reload guard and the next mount inherits
   *     stale BehaviorSubject values (sheetLevelStatusSubject in
   *     particular), leaking `hasSuggestedChanges` / status-lock state into
   *     the parity check and surfacing as a "[ParityCheck] … parity
   *     mismatch" throw on the first sheet that re-runs validation.
   *   - `mbrs-validation-cache-*`           (validationCache.clearAll)
   */
  private flushConversionCaches(): void {
    this.clearAll();
    this.filingFormState.clearAllPersistedFormState();
    this.filingFormState.clearAllInMemoryFormState();
    this.validationCache.clearAll();
  }

  /**
   * Read the last-visited sheet for a conversion. Async because the pointer
   * now lives in IndexedDB. Scopes a null id to `'new'`, matching the legacy
   * helper (the persistence key builder also coerces falsy ids to `'new'`).
   */
  get(conversionId: string | null): Promise<string | null> {
    return this.persistence.getCurrentSheet(conversionId ?? 'new');
  }

  set(conversionId: string | null, sheetValue: string): void {
    this.persistence.putCurrentSheet(conversionId ?? 'new', sheetValue);
  }

  clear(conversionId: string | null): void {
    // Fire-and-forget: drop the per-conversion pointer; failures are handled
    // inside the persistence layer.
    void this.persistence.removeCurrentSheet(conversionId ?? 'new');
  }

  /**
   * Wipes every `mbrs-current-sheet-*` entry. Used when the user
   * navigates out of the filing form to any non-filing page — at that
   * point no conversion is "active" and re-entering any conversion
   * should start fresh. Fire-and-forget.
   */
  clearAll(): void {
    void this.persistence.clearAllCurrentSheets();
  }
}
